import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import SignOut from '../pages/SignOut';
import eastbadge_logo from '../assets/eastbadge_logo_blue.png';

import {
  AppBar, Toolbar, Typography, Button, Box, Container, Menu, MenuItem,
  IconButton, useMediaQuery, Drawer, Collapse, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const LayoutHeader = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');

  const [anchorEl, setAnchorEl] = useState(null);
  const [productsMenu, setProductsMenu] = useState(null);
  const [companyMenu, setCompanyMenu] = useState(null);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [showCompany, setShowCompany] = useState(false);

  const openAccountMenu = (event) => {
    document.body.style.overflow = 'hidden';
    setAnchorEl(event.currentTarget);
  };

  const closeAccountMenu = () => {
    document.body.style.overflow = 'auto';
    setAnchorEl(null);
  };

  const openProductsMenu = (event) => {
    document.body.style.overflow = 'hidden';
    setProductsMenu(event.currentTarget);
  };

  const closeProductsMenu = () => {
    document.body.style.overflow = 'auto';
    setProductsMenu(null);
  };

  const openCompanyMenu = (event) => {
    document.body.style.overflow = 'hidden';
    setCompanyMenu(event.currentTarget);
  };

  const closeCompanyMenu = () => {
    document.body.style.overflow = 'auto';
    setCompanyMenu(null);
  };

  const goToAccount = () => {
    navigate('/account');
    setMobileMenuOpen(false);
  };

  const goToSignIn = () => {
    navigate('/signin', { state: { from: location } });
    setMobileMenuOpen(false);
  };

  const drawerTopOffset = isMobile ? 70 : 120; // Same as AppBar height

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: '#fff',
          color: '#fff',
          minHeight: { xs: 70, sm: 120 },
          boxShadow: '0px 6px 6px -2px rgba(0, 0, 0, 0.1)',
          '@media (max-width:600px)': { minHeight: 64 }
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: { xs: '0px', sm: '20px', md: '40px' },
              mt: { xs: 2, sm: 3, md: 3 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                width: { xs: '60%', sm: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                alignItems: 'center',
                ml: { xs: 0, sm: 3, md: 3 }
              }}
            >
              <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <img
                  src={eastbadge_logo}
                  alt="EastBadge Logo"
                  style={{
                    height: '50px',
                    width: 'auto',
                    objectFit: 'contain',
                    maxWidth: isMobile ? '150px' : undefined,
                  }}
                />
              </RouterLink>
            </Box>

            {/* Navigation */}
            <Box
              sx={{
                width: { xs: '50%', sm: 'auto' },
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: { xs: 1, sm: 3 },
                flexGrow: 1,
              }}
            >
              {/* MOBILE: Hamburger */}
              {isMobile && (
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{ color: '#04003F', ml: 12 }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {/* DESKTOP: Products & Company */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    onClick={openProductsMenu}
                    sx={{ color: '#04003F', textTransform: 'none', fontSize: '1rem', '&:hover': { backgroundColor: '#fafafa' } }}
                  >
                    Products
                  </Button>
                  <Menu anchorEl={productsMenu} open={Boolean(productsMenu)} onClose={closeProductsMenu}>
                    <Box sx={{ p: 2, width: { xs: '280px', sm: '300px', md: '340px' } }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#04003F' }}>
                        Ace with Mocks
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', color: '#04003F', mb: 1 }}>
                        Practice mock interviews with experts. Simulate real interview experience and ace your interviews.
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                        <MenuItem
                          sx={{ color: '#F5973A', flex: 1, textAlign: 'center' }}
                          onClick={() => {
                            navigate("/coaches?product=Ace%20with%20Mocks&role=&company=");
                            closeProductsMenu();
                          }}
                        >
                          Browse Experts
                        </MenuItem>
                        <MenuItem
                          component={RouterLink}
                          to="/become-an-expert"
                          sx={{ color: '#F5973A', flex: 1, textAlign: 'center' }}
                          onClick={closeProductsMenu}
                        >
                          Become an Expert
                        </MenuItem>
                      </Box>
                    </Box>
                  </Menu>
                </Box>

                <Box sx={{ position: 'relative' }}>
                  <Button
                    onClick={openCompanyMenu}
                    sx={{ color: '#04003F', textTransform: 'none', fontSize: '1rem', '&:hover': { backgroundColor: '#fafafa' } }}
                  >
                    Company
                  </Button>
                  <Menu 
                    anchorEl={companyMenu} 
                    open={Boolean(companyMenu)} 
                    onClose={closeCompanyMenu}
                    slotProps={{
                      paper: {
                        sx: {
                          width: { xs: '200px', sm: '220px', md: '300px' }, 
                          maxWidth: '90vw', // Optional: ensures responsiveness on smaller screens
                        },
                      },
                    }}
                  >
                    <MenuItem
                      component={RouterLink}
                      to="/faqs"
                      sx={{ color: '#F5973A' }}
                      onClick={closeCompanyMenu}
                    >
                      FAQs
                    </MenuItem>

                    <MenuItem
                      component="a"
                      href="https://www.trustpilot.com/review/eastbadge.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#F5973A',
                        height: '42px',
                      }}
                      onClick={closeCompanyMenu}
                    >
                      Trustpilot Reviews
                    </MenuItem>

                    <MenuItem
                      component="a"
                      href="https://discord.gg/Hjs2mNcezm"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: '#F5973A',
                        height: '42px',
                      }}
                      onClick={closeCompanyMenu}
                    >
                      Join Our Discord
                    </MenuItem>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0,
                        ml: 2,
                        mr: 2,
                        mb: 1,
                        textAlign: 'left',
                        fontSize: '0.85rem', 
                        color: '#04003F',                         
                      }}
                    >
                      Access prep resources, chat with coaches, and connect with community.
                    </Typography>
                    
                  </Menu>
                </Box>
              </Box>
            </Box>

            {/* DESKTOP: Auth buttons */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              {currentUser ? (
                <>
                  <Button
                    onClick={openAccountMenu}
                    sx={{ color: '#04003F', textTransform: 'none', fontSize: '1rem', gap: '5px', mr: 9, '&:hover': { backgroundColor: '#fafafa' } }}
                  >
                    <AccountCircleIcon sx={{ fontSize: 24 }} />
                    <Box
                      component="span"
                      sx={{
                        display: { xs: 'inline', sm: 'none', md: 'inline' },
                      }}
                    >
                      Account
                    </Box>
                  </Button>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeAccountMenu}>
                    <MenuItem onClick={goToAccount} sx={{ color: '#F5973A' }}>
                      My Account
                    </MenuItem>
                    <MenuItem onClick={closeAccountMenu} sx={{ color: '#F5973A' }}>
                      <Typography sx={{ lineHeight: 'normal' }}>
                        <SignOut />
                      </Typography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  onClick={goToSignIn}
                  sx={{ color: '#04003F', textTransform: 'none', fontSize: '1rem', mr: 9, '&:hover': { backgroundColor: '#fafafa' } }}
                >
                  Register/ Sign In
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* MOBILE: Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        hideBackdrop
        PaperProps={{
          sx: {
            top: `${drawerTopOffset}px`,
            height: `calc(100% - ${drawerTopOffset}px)`,
            width: '100%',
            backgroundColor: '#04003F'
          }
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box display="flex" justifyContent="flex-end" p={1}>
          <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box p={2}>
          <Button
            fullWidth
            onClick={() => setShowProducts(!showProducts)}
            endIcon={showProducts ? <ExpandLess /> : <ExpandMore />}
            sx={{ color: '#F5973A', textTransform: 'none', justifyContent: 'flex-start', fontSize: '1rem'  }}
          >
            Products
          </Button>
          <Collapse in={showProducts}>
            <Box sx={{ pl: 1, py: 0 }}>
              <Typography sx={{ fontSize: '1rem', color: 'white', mb: 1 }}>
                Ace with Mocks
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: 'white', mb: 1 }}>
                Practice mock interviews with experts. Simulate real interview experience and ace your interviews.
              </Typography>
              <MenuItem
                onClick={() => {
                  navigate("/coaches?product=Ace%20with%20Mocks&role=&company=");
                  setMobileMenuOpen(false);
                }}
                sx={{ color: 'white', fontSize: '1rem', fontWeight: 600, minHeight: '40px', lineHeight: 1.2 }}
              >
                Browse Experts
              </MenuItem>
              <MenuItem
                component={RouterLink}
                to="/become-an-expert"
                onClick={() => setMobileMenuOpen(false)}
                sx={{ color: 'white', fontSize: '1rem', fontWeight: 600, minHeight: '40px', lineHeight: 1.2 }}
              >
                Become an Expert
              </MenuItem>
            </Box>
          </Collapse>

          <Divider sx={{ my: 2 }} />
          <Button
            fullWidth
            onClick={() => setShowCompany(!showCompany)}
            endIcon={showCompany ? <ExpandLess /> : <ExpandMore />}
            sx={{ color: '#F5973A', textTransform: 'none', justifyContent: 'flex-start', fontSize: '1rem'  }}
          >
            Company
          </Button>
          <Collapse in={showCompany}>
            <MenuItem
              component={RouterLink}
              to="/faqs"
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: 'white', fontSize: '1rem', fontWeight: 600, pl: 1, minHeight: '40px', lineHeight: 1.2 }}
            >
              FAQs
            </MenuItem>

            <MenuItem
              component="a"
              href="https://www.trustpilot.com/review/eastbadge.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: 'white', fontSize: '1rem', fontWeight: 600, pl: 1, minHeight: '42px' }}
            >
              Trustpilot Reviews
            </MenuItem>

            <MenuItem
              component="a"
              href="https://discord.gg/Hjs2mNcezm"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: 'white', fontSize: '1rem', fontWeight: 600, pl: 1, minHeight: '42px' }}
            >
              Join Our Discord
            </MenuItem>
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                fontSize: '0.8rem',
                pl: 1,
                mt: 0,
                mb: 1,
              }}
            >
              Access prep resources, chat with coaches, and connect with community.
            </Typography>
            
          </Collapse>
        </Box>

        {/* Auth Buttons in Drawer Bottom */}
        <Box mt="auto" p={2}>
          <Divider sx={{ mb: 2 }} />
          {currentUser ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button 
                  onClick={goToAccount}
                  variant="contained" 
                  sx={{ 
                    backgroundImage: 'linear-gradient(180deg, #F5973A 0%, #FFD23E 100%)',
                    color: '#04003F',
                    textTransform: 'capitalize',
                    height: '47px', 
                    width: '50%',            
                    borderRadius: '25px',
                    mb: '10px',
                    fontSize: '1.0rem',
                    '&:hover': { 
                      backgroundImage: 'linear-gradient(180deg, #ff870f 0%, #ffd102 100%)'
                    },
                  }}
                >
                  My Account
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  '& p': {
                    backgroundImage: 'linear-gradient(180deg, #F5973A 0%, #FFD23E 100%)',
                    color: '#04003F',
                    borderRadius: '25px',
                    padding: '14px 2px',
                    textAlign: 'center',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    width: '50%',
                    mb: '10px',
                    '&:hover': {
                      backgroundImage: 'linear-gradient(180deg, #ff870f 0%, #ffd102 100%)'
                    }
                  }
                }}
              >
                <SignOut />
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button 
                fullWidth 
                variant="contained" 
                sx={{ 
                  backgroundImage: 'linear-gradient(180deg, #F5973A 0%, #FFD23E 100%)',
                  color: '#04003F',
                  textTransform: 'capitalize',
                  height: '47px', 
                  width: '50%',            
                  borderRadius: '25px',
                  ml: 'auto',
                  mr: 'auto',
                  mb: '10px',
                  fontSize: '1.0rem',
                  '&:hover': { 
                    backgroundImage: 'linear-gradient(180deg, #ff870f 0%, #ffd102 100%)'
                  },
                }}
                onClick={goToSignIn}
              >
                Register/ Sign In
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default LayoutHeader;
